import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  CreditCard, Building, Plus, Trash2, DollarSign, RefreshCw,
  User, Clock, CheckCircle, AlertCircle, Repeat
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const statusColors = {
  processed: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
};

export default function PaymentsPage() {
  const [activeTab, setActiveTab] = useState('virtual-terminal');
  const [merchantAccount, setMerchantAccount] = useState(null);
  const [cardProfiles, setCardProfiles] = useState([]);
  const [achProfiles, setAchProfiles] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalTxns, setTotalTxns] = useState(0);
  const [loading, setLoading] = useState(true);

  // Profile dialog
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileType, setProfileType] = useState('card');
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  // Transaction dialog
  const [txnDialogOpen, setTxnDialogOpen] = useState(false);
  const [txnForm, setTxnForm] = useState({ profile_id: '', amount: '', description: '', direction: 'charge', recurring: false, recurring_frequency: 'monthly', recurring_day: 1 });
  const [processingTxn, setProcessingTxn] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [acct, cards, ach, txnData] = await Promise.all([
        api.getMyMerchantAccount(),
        api.getPaymentProfiles('card').catch(() => []),
        api.getPaymentProfiles('ach').catch(() => []),
        api.getTransactions().catch(() => ({ transactions: [], total: 0 })),
      ]);
      setMerchantAccount(acct);
      setCardProfiles(cards);
      setAchProfiles(ach);
      setTransactions(txnData.transactions || []);
      setTotalTxns(txnData.total || 0);
    } catch {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const openNewProfile = (type) => {
    setProfileType(type);
    setProfileForm(type === 'card'
      ? { profile_name: '', card_holder_name: '', card_number_encrypted: '', card_expiry: '', card_brand: '', billing_address: '', billing_city: '', billing_state: '', billing_zip: '', entity_label: 'merchant_customer' }
      : { profile_name: '', account_holder_name: '', bank_name: '', routing_number: '', account_number_encrypted: '', account_type: 'checking', entity_label: 'iso_downline' }
    );
    setProfileDialogOpen(true);
  };

  const saveProfile = async () => {
    if (!profileForm.profile_name) { toast.error('Profile name required'); return; }
    setSavingProfile(true);
    try {
      const last4Field = profileType === 'card' ? 'card_number_last4' : 'account_number_last4';
      const encField = profileType === 'card' ? 'card_number_encrypted' : 'account_number_encrypted';
      const num = profileForm[encField] || '';
      const data = {
        ...profileForm,
        profile_type: profileType,
        [last4Field]: num.slice(-4),
      };
      await api.createPaymentProfile(data);
      toast.success('Profile saved');
      setProfileDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const deleteProfile = async (id) => {
    try {
      await api.deletePaymentProfile(id);
      toast.success('Profile deleted');
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  const openCharge = (type, direction = 'charge') => {
    const profiles = type === 'card' ? cardProfiles : achProfiles;
    setTxnForm({
      profile_id: profiles[0]?.id || '',
      payment_type: type,
      direction,
      amount: '',
      description: '',
      recurring: false,
      recurring_frequency: 'monthly',
      recurring_day: 1,
    });
    setTxnDialogOpen(true);
  };

  const processTransaction = async () => {
    if (!txnForm.profile_id) { toast.error('Select a profile'); return; }
    if (!txnForm.amount || parseFloat(txnForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setProcessingTxn(true);
    try {
      await api.createTransaction({
        ...txnForm,
        amount: parseFloat(txnForm.amount),
      });
      toast.success('Transaction processed');
      setTxnDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Transaction failed');
    } finally {
      setProcessingTxn(false);
    }
  };

  const setPF = (k, v) => setProfileForm((f) => ({ ...f, [k]: v }));
  const setTF = (k, v) => setTxnForm((f) => ({ ...f, [k]: v }));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (merchantAccount?.status !== 'active') {
    return (
      <div className="flex items-center justify-center h-64" data-testid="payments-not-active">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Payment Account Not Active</h2>
          <p className="text-sm text-muted-foreground">
            {merchantAccount?.status === 'pending'
              ? 'Your merchant application is under review. You will be able to process payments once approved.'
              : 'You need an active merchant account to process payments. Go to Settings to apply.'}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Process card and ACH transactions</p>
        </div>
        <Button variant="outline" size="icon" onClick={loadAll}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="virtual-terminal" data-testid="payments-tab-vt"><CreditCard className="h-4 w-4 mr-2" /> Virtual Terminal</TabsTrigger>
          <TabsTrigger value="ach" data-testid="payments-tab-ach"><Building className="h-4 w-4 mr-2" /> ACH</TabsTrigger>
          <TabsTrigger value="history" data-testid="payments-tab-history"><Clock className="h-4 w-4 mr-2" /> History</TabsTrigger>
        </TabsList>

        {/* ── Virtual Terminal Tab ──────────────────────── */}
        <TabsContent value="virtual-terminal">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Card Profiles */}
            <Card className="p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Card Profiles</h3>
                <Button size="sm" className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={() => openNewProfile('card')} data-testid="add-card-profile-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Card
                </Button>
              </div>
              {cardProfiles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No card profiles yet. Add one to start processing.</p>
              ) : (
                <div className="space-y-3">
                  {cardProfiles.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors" data-testid={`card-profile-${p.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <CreditCard className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{p.profile_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {p.card_brand || 'Card'} ****{p.card_number_last4 || '----'}
                            {p.entity_label ? ` · ${p.entity_label.replace(/_/g, ' ')}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => { openCharge('card'); setTF('profile_id', p.id); }} data-testid={`charge-card-${p.id}`}>
                          <DollarSign className="h-3.5 w-3.5 mr-1" /> Charge
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => deleteProfile(p.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Quick Charge */}
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4">Quick Charge</h3>
              <Button className="w-full bg-[#3b82f6] hover:bg-[#2563eb] text-white mb-2" onClick={() => openCharge('card')} disabled={cardProfiles.length === 0} data-testid="quick-card-charge-btn">
                <CreditCard className="h-4 w-4 mr-2" /> New Card Charge
              </Button>
              <p className="text-xs text-muted-foreground text-center">One-time or recurring</p>
            </Card>
          </div>
        </TabsContent>

        {/* ── ACH Tab ──────────────────────────────────── */}
        <TabsContent value="ach">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* ACH Profiles */}
            <Card className="p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">ACH Profiles</h3>
                <Button size="sm" className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={() => openNewProfile('ach')} data-testid="add-ach-profile-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add ACH
                </Button>
              </div>
              {achProfiles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No ACH profiles yet. Add one to start transfers.</p>
              ) : (
                <div className="space-y-3">
                  {achProfiles.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors" data-testid={`ach-profile-${p.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Building className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{p.profile_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {p.bank_name || 'Bank'} ****{p.account_number_last4 || '----'} ({p.account_type || 'checking'})
                            {p.entity_label ? ` · ${p.entity_label.replace(/_/g, ' ')}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => { openCharge('ach', 'credit'); setTF('profile_id', p.id); }} data-testid={`ach-credit-${p.id}`}>
                          Credit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { openCharge('ach', 'debit'); setTF('profile_id', p.id); }} data-testid={`ach-debit-${p.id}`}>
                          Debit
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => deleteProfile(p.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Quick Actions */}
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Button className="w-full" variant="outline" onClick={() => openCharge('ach', 'credit')} disabled={achProfiles.length === 0} data-testid="quick-ach-credit-btn">
                  Pay Downline (Credit)
                </Button>
                <Button className="w-full" variant="outline" onClick={() => openCharge('ach', 'debit')} disabled={achProfiles.length === 0} data-testid="quick-ach-debit-btn">
                  Collect Payment (Debit)
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* ── Transaction History ───────────────────────── */}
        <TabsContent value="history">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="transactions-table">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="px-6 py-3 font-medium">Date</th>
                    <th className="px-6 py-3 font-medium">Type</th>
                    <th className="px-6 py-3 font-medium">Direction</th>
                    <th className="px-6 py-3 font-medium">Profile</th>
                    <th className="px-6 py-3 font-medium">Amount</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 font-medium">Recurring</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">No transactions yet</td></tr>}
                  {transactions.map((t, idx) => (
                    <tr key={t.id || idx} className="border-b last:border-0 hover:bg-accent/50 transition-colors">
                      <td className="px-6 py-4 text-sm">{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-medium px-2 py-1 bg-accent rounded uppercase">{t.payment_type}</span>
                      </td>
                      <td className="px-6 py-4 text-sm capitalize">{t.direction}</td>
                      <td className="px-6 py-4 text-sm">{t.profile_name || '-'}</td>
                      <td className="px-6 py-4 text-sm font-medium">${t.amount?.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[t.status] || 'bg-gray-100'}`}>
                          {t.status === 'processed' && <CheckCircle className="h-3 w-3" />}
                          {t.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {t.recurring && <span className="inline-flex items-center gap-1 text-xs text-blue-600"><Repeat className="h-3 w-3" /> {t.recurring_frequency}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── New Profile Dialog ──────────────────────────── */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{profileType === 'card' ? 'Add Card Profile' : 'Add ACH Profile'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Profile Name *</Label>
              <Input placeholder={profileType === 'card' ? 'e.g. John Smith - Visa' : 'e.g. Agent Martinez - Chase'} value={profileForm.profile_name || ''} onChange={(e) => setPF('profile_name', e.target.value)} className="mt-1" data-testid="profile-name-input" />
            </div>
            <div>
              <Label>Label</Label>
              <Select value={profileForm.entity_label || ''} onValueChange={(v) => setPF('entity_label', v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select label" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="merchant_customer">Merchant Customer</SelectItem>
                  <SelectItem value="iso_downline">ISO Downline</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {profileType === 'card' ? (
              <>
                <div><Label>Cardholder Name</Label><Input value={profileForm.card_holder_name || ''} onChange={(e) => setPF('card_holder_name', e.target.value)} className="mt-1" data-testid="card-holder-name" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Card Number</Label><Input placeholder="4111111111111111" value={profileForm.card_number_encrypted || ''} onChange={(e) => setPF('card_number_encrypted', e.target.value)} className="mt-1 font-mono" data-testid="card-number-input" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Expiry</Label><Input placeholder="MM/YY" value={profileForm.card_expiry || ''} onChange={(e) => setPF('card_expiry', e.target.value)} className="mt-1 font-mono" /></div>
                    <div><Label>Brand</Label><Input placeholder="Visa" value={profileForm.card_brand || ''} onChange={(e) => setPF('card_brand', e.target.value)} className="mt-1" /></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2"><Label>Billing Address</Label><Input value={profileForm.billing_address || ''} onChange={(e) => setPF('billing_address', e.target.value)} className="mt-1" /></div>
                  <div><Label>City</Label><Input value={profileForm.billing_city || ''} onChange={(e) => setPF('billing_city', e.target.value)} className="mt-1" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>State</Label><Input value={profileForm.billing_state || ''} onChange={(e) => setPF('billing_state', e.target.value)} className="mt-1" /></div>
                    <div><Label>ZIP</Label><Input value={profileForm.billing_zip || ''} onChange={(e) => setPF('billing_zip', e.target.value)} className="mt-1" /></div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div><Label>Account Holder Name</Label><Input value={profileForm.account_holder_name || ''} onChange={(e) => setPF('account_holder_name', e.target.value)} className="mt-1" data-testid="ach-holder-name" /></div>
                <div><Label>Bank Name</Label><Input value={profileForm.bank_name || ''} onChange={(e) => setPF('bank_name', e.target.value)} className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Routing Number</Label><Input value={profileForm.routing_number || ''} onChange={(e) => setPF('routing_number', e.target.value)} className="mt-1 font-mono" data-testid="ach-routing-input" /></div>
                  <div><Label>Account Number</Label><Input value={profileForm.account_number_encrypted || ''} onChange={(e) => setPF('account_number_encrypted', e.target.value)} className="mt-1 font-mono" data-testid="ach-account-input" /></div>
                </div>
                <div>
                  <Label>Account Type</Label>
                  <Select value={profileForm.account_type || 'checking'} onValueChange={(v) => setPF('account_type', v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="checking">Checking</SelectItem>
                      <SelectItem value="savings">Savings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn">
              {savingProfile ? 'Saving...' : 'Save Profile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Transaction Dialog ──────────────────────────── */}
      <Dialog open={txnDialogOpen} onOpenChange={setTxnDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {txnForm.payment_type === 'card' ? 'Process Card Payment' : `ACH ${txnForm.direction === 'credit' ? 'Credit (Pay Out)' : 'Debit (Collect)'}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Select Profile *</Label>
              <Select value={txnForm.profile_id} onValueChange={(v) => setTF('profile_id', v)}>
                <SelectTrigger className="mt-1" data-testid="txn-profile-select"><SelectValue placeholder="Choose profile" /></SelectTrigger>
                <SelectContent>
                  {(txnForm.payment_type === 'card' ? cardProfiles : achProfiles).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.profile_name} (****{p.card_number_last4 || p.account_number_last4 || '----'})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount ($) *</Label>
              <Input type="number" step="0.01" min="0.01" value={txnForm.amount} onChange={(e) => setTF('amount', e.target.value)} className="mt-1 font-mono text-lg" placeholder="0.00" data-testid="txn-amount-input" />
            </div>
            <div>
              <Label>Description / Memo</Label>
              <Input value={txnForm.description || ''} onChange={(e) => setTF('description', e.target.value)} className="mt-1" placeholder="e.g. Clover Station purchase" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={txnForm.recurring}
                onCheckedChange={(v) => setTF('recurring', v)}
                id="recurring-check"
              />
              <Label htmlFor="recurring-check" className="cursor-pointer">Make this a recurring payment</Label>
            </div>
            {txnForm.recurring && (
              <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-blue-200">
                <div>
                  <Label>Frequency</Label>
                  <Select value={txnForm.recurring_frequency} onValueChange={(v) => setTF('recurring_frequency', v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {txnForm.recurring_frequency === 'monthly' && (
                  <div>
                    <Label>Day of Month</Label>
                    <Input type="number" min={1} max={28} value={txnForm.recurring_day} onChange={(e) => setTF('recurring_day', parseInt(e.target.value) || 1)} className="mt-1" />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTxnDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={processTransaction} disabled={processingTxn} data-testid="process-txn-btn">
              {processingTxn ? 'Processing...' : `Process $${txnForm.amount || '0.00'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
